// src/pages/dashboard/alumnos/components/CambiarFamiliaModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Table, Input, Button, message, Spin, Card, Tag, Space, Row, Col } from 'antd';
import { SearchOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import apiClient from '../../../../api/apiClient';

const CambiarFamiliaModal = ({ open, onCancel, alumnoData, familiaActual, onFamiliaCambiada }) => {
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [familiaSeleccionada, setFamiliaSeleccionada] = useState(null);
  const [responsables, setResponsables] = useState([]);
  const [loadingResponsables, setLoadingResponsables] = useState(false);
  const [loadingCambio, setLoadingCambio] = useState(false);

  // Cargar todas las familias al abrir el modal
  useEffect(() => {
    if (open) {
      cargarFamilias();
      // Limpiar selección anterior
      setFamiliaSeleccionada(null);
      setResponsables([]);
      setBusqueda('');
    }
  }, [open]);

  const cargarFamilias = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/familias');
      let lista = res.data.data || res.data;
      if (!Array.isArray(lista)) {
        lista = Object.values(lista);
      }
      setFamilias(lista);
    } catch (error) {
      message.error('Error al cargar familias');
      setFamilias([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarResponsables = async (idFamilia) => {
    setLoadingResponsables(true);
    try {
      const res = await apiClient.get(`/responsables/familia/${idFamilia}`);
      const lista = Array.isArray(res.data) ? res.data : res.data.data || [];
      setResponsables(lista);
    } catch (error) {
      setResponsables([]);
    } finally {
      setLoadingResponsables(false);
    }
  };

  const handleSeleccionarFamilia = (familia) => {
    setFamiliaSeleccionada(familia);
    cargarResponsables(familia.IdFamilia);
  };

  const handleConfirmarCambio = async () => {
    if (!familiaSeleccionada) {
      message.warning('Selecciona una familia primero');
      return;
    }

    if (familiaSeleccionada.IdFamilia === familiaActual?.IdFamilia) {
      message.info('El alumno ya pertenece a esta familia');
      return;
    }

    const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
    const IdColaborador = userFromStorage.IdUsuario;

    if (!IdColaborador) {
      message.error('No se encontró el usuario en sesión.');
      return;
    }

    setLoadingCambio(true);
    try {
      await apiClient.put(`/alumnos/${alumnoData.IdAlumno}`, {
        IdFamilia: familiaSeleccionada.IdFamilia,
        IdColaborador,
      });

      message.success(`Familia cambiada a "${familiaSeleccionada.NombreFamilia}" correctamente`);
      onFamiliaCambiada(familiaSeleccionada);
      onCancel();
    } catch (error) {
      message.error('Error al cambiar la familia del alumno');
    } finally {
      setLoadingCambio(false);
    }
  };

  // Filtrar familias por búsqueda
  const familiasFiltradas = familias.filter(f => {
    if (!busqueda.trim()) return true;
    const term = busqueda.toLowerCase();
    return (
      (f.NombreFamilia || '').toLowerCase().includes(term) ||
      String(f.IdFamilia || '').includes(busqueda)
    );
  });

  const columnas = [
    {
      title: 'ID',
      dataIndex: 'IdFamilia',
      width: 70,
      align: 'center',
      render: (text) => <Tag color="purple">{text}</Tag>
    },
    {
      title: 'Nombre de Familia',
      dataIndex: 'NombreFamilia',
      width: 250,
    },
    {
      title: 'Acción',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Button
          type={familiaSeleccionada?.IdFamilia === record.IdFamilia ? 'primary' : 'default'}
          size="small"
          onClick={() => handleSeleccionarFamilia(record)}
        >
          {familiaSeleccionada?.IdFamilia === record.IdFamilia ? 'Seleccionada' : 'Seleccionar'}
        </Button>
      )
    }
  ];

  return (
    <Modal
      title={
        <Space>
          <TeamOutlined />
          <span>Cambiar Familia del Alumno</span>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={900}
      destroyOnClose
    >
      {/* Info del alumno actual */}
      <div style={{ marginBottom: 16, padding: 12, background: '#e6f7ff', borderRadius: 8, border: '1px solid #91d5ff' }}>
        <Row gutter={16}>
          <Col span={12}>
            <strong>Alumno:</strong> {alumnoData?.Nombres} {alumnoData?.Apellidos}
          </Col>
          <Col span={12}>
            <strong>Familia actual:</strong>{' '}
            <Tag color="blue">{familiaActual?.NombreFamilia || 'No asignada'}</Tag>
          </Col>
        </Row>
      </div>

      {/* Buscador */}
      <Input
        placeholder="Buscar familia por nombre o ID..."
        prefix={<SearchOutlined />}
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        size="large"
        style={{ marginBottom: 16 }}
        allowClear
      />

      {/* Tabla de familias */}
      <Table
        columns={columnas}
        dataSource={familiasFiltradas}
        rowKey="IdFamilia"
        loading={loading}
        size="small"
        pagination={{
          pageSize: 8,
          showSizeChanger: false,
          showTotal: (total) => `${total} familias`
        }}
        scroll={{ y: 300 }}
        onRow={(record) => ({
          onClick: () => handleSeleccionarFamilia(record),
          style: {
            cursor: 'pointer',
            backgroundColor: familiaSeleccionada?.IdFamilia === record.IdFamilia ? '#e6f7ff' : undefined
          }
        })}
      />

      {/* Responsables de la familia seleccionada */}
      {familiaSeleccionada && (
        <Card
          title={
            <span>
              <UserOutlined /> Responsables de familia: <strong>{familiaSeleccionada.NombreFamilia}</strong>
            </span>
          }
          size="small"
          style={{ marginTop: 16 }}
        >
          {loadingResponsables ? (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Spin />
            </div>
          ) : responsables.length > 0 ? (
            <div>
              {responsables.map((r, index) => (
                <div
                  key={r.IdResponsable || index}
                  style={{
                    padding: 10,
                    marginBottom: 8,
                    background: '#fafafa',
                    borderRadius: 6,
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <Row gutter={16}>
                    <Col span={8}>
                      <strong>{r.NombreResponsable}</strong>
                    </Col>
                    <Col span={5}>
                      <Tag color="geekblue">{r.TipoResponsable || 'Responsable'}</Tag>
                    </Col>
                    <Col span={5}>
                      DPI: {r.DPI || '-'}
                    </Col>
                    <Col span={6}>
                      Tel: {r.TelefonoContacto || '-'}
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: 12 }}>
              Esta familia no tiene responsables registrados
            </div>
          )}
        </Card>
      )}

      {/* Botones */}
      <div style={{ textAlign: 'right', marginTop: 24 }}>
        <Button onClick={onCancel} style={{ marginRight: 12 }}>
          Cancelar
        </Button>
        <Button
          type="primary"
          size="large"
          onClick={handleConfirmarCambio}
          loading={loadingCambio}
          disabled={!familiaSeleccionada || familiaSeleccionada.IdFamilia === familiaActual?.IdFamilia}
        >
          Confirmar Cambio de Familia
        </Button>
      </div>
    </Modal>
  );
};

export default CambiarFamiliaModal;
